/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import Attributes from "./Attributes";
import AttributeValues from "./AttributeValues";
import SkillStats from "./SkillStats";

export class Skill {
    attribute: (keyof AttributeValues) | 'unknown' = 'unknown';
    bonus: number = 0; // used if the character has a bonus to this skill independant of proficiency/expertise
    level: "unskilled" | "proficient" | "expert" = "unskilled";
    adjLevel: number = 0; // combines the attribute's bonus, the character proficiency/expertise bonus and the 'bonus' value
}

class SkillSet {
    custom: string | null = null;
    skills: Record<string, Skill> = {};

    public static updateLevels = (skillSet: SkillSet, attributes: AttributeValues, proficiency: number) => {
        const newSkills = new SkillSet();

        for (const skillName in skillSet.skills) {
            const skill = skillSet.skills[skillName];
            if (skill && skill.attribute != 'unknown') {
                const attrLevel = attributes[skill.attribute];
                const attrBonus = Attributes.getBonus(attrLevel);
                if (skill.level === 'expert') {
                    skill.adjLevel = attrBonus + proficiency * 2 + skill.bonus;
                } else if (skill.level === 'proficient') {
                    skill.adjLevel = attrBonus + proficiency + skill.bonus;
                } else {
                    skill.adjLevel = attrBonus + skill.bonus;
                }
            }
            newSkills.skills[skillName] = {...skill};
        }
        newSkills.custom = SkillSet.getString(newSkills);
        return newSkills;
    }

    public static getString = (skillSet: SkillSet) => {
        const allCustoms = skillSet.custom?.split(',');
        let lastCustom = allCustoms ? allCustoms[allCustoms.length-1] : '';
        lastCustom = lastCustom.endsWith(' ') ? (lastCustom.trim() + ' ') : lastCustom.trim();
        const skillStrings = [];
        for(const skillName of SkillStats.getSkillsMap().keys()) {
            const skill = skillSet.skills[skillName];
            if (skill && skill.level != 'unskilled') {
                let levelString = skillName + ' ';
                if (skill.adjLevel >= 0) {
                    levelString += '+';
                }
                levelString += skill.adjLevel;
                skillStrings.push(levelString);
            }
        }
        for (const skillName in skillSet.skills) {
            const skill = skillSet.skills[skillName];
            if (skill && skill.attribute === 'unknown') {
                let levelString = skillName + ' ';
                if (skill.adjLevel >= 0) {
                    levelString += '+';
                }
                levelString += skill.adjLevel;
                skillStrings.push(levelString);
            }
        }
        if (!skillStrings.includes(lastCustom.trim())) {
            if (skillStrings.length) {
                if (lastCustom === '' || lastCustom === ' ') {
                    return skillStrings.join(', ') + ',' + lastCustom;
                }
            }
            if (allCustoms) {
                skillStrings.push(lastCustom);
            }
        }
        return skillStrings.join(', ');
    }

    public static parseFromString = (source: string, attributes: AttributeValues, proficiencyBonus: number): SkillSet => {
        const skillSet = new SkillSet();
        skillSet.custom = source.trim() + (source.endsWith(' ') ? ' ' : '');
        skillSet.skills = {};
        for (const rawSkillName of source.split(',')) {
            let skillName = rawSkillName.trim().toLowerCase();
            const skillWords = skillName.split(' ');
            let valStr = skillWords[skillWords.length-1].trim();
            if (!/^[+-]?\d+(\.\d+)?$/.test(valStr)) {
                valStr = '';
            }

            for (const trueSkillName of SkillStats.getSkillsMap().keys()) {
                if (skillName.startsWith(trueSkillName.toLowerCase())) {
                    valStr = skillName.substring(trueSkillName.length).trim()
                    skillName = trueSkillName;
                    break;
                }
            }
            const attr = SkillStats.getSkillsMap().get(skillName);
            const skill = new Skill();
            skill.attribute = attr ?? 'unknown';
            skill.adjLevel = valStr ? parseInt(valStr) : 0;
            if (attr) {
                const attrBonus = Attributes.getBonus(attributes[attr]);
                const remainingVal = skill.adjLevel - attrBonus;
                if (remainingVal >= proficiencyBonus * 2) {
                    skill.level = 'expert';
                    skill.bonus = remainingVal - (proficiencyBonus * 2);
                } else if (remainingVal >= proficiencyBonus) {
                    skill.level = 'proficient';
                    skill.bonus = remainingVal - proficiencyBonus;
                } else {
                    skill.level = 'unskilled';
                    skill.bonus = remainingVal;
                }
            } else {
                skillName = rawSkillName.trim().substring(0, skillName.length - valStr.length).trim();
                skill.level = 'unskilled';
                skill.bonus = skill.adjLevel;
            }
            if (skillName && valStr) {
                skillSet.skills[skillName] = skill;
            }
        }
        return skillSet;
    }

    public static parseFromObject = (source: any, attributes: AttributeValues, proficiencyBonus: number): SkillSet => {
        // source is assumed to be in the form:
        //   {"perception": 3, "stealth": 6}
        const skillSet = new SkillSet();
        const skillEntries = Object.entries(source); // Get entries from the original object
        for (const [key, value] of skillEntries) {
            const name = key.substring(0, 1).toUpperCase() + key.substring(1).toLowerCase();
            const attr = SkillStats.getSkillsMap().get(name);
            if (attr) {
                const val = (typeof value === 'number') ? value :  (typeof value === 'string') ? parseInt(value) : 0;
                const attrBonus = Attributes.getBonus(attributes[attr]);
                const remainingVal = val - attrBonus;
                const sk = new Skill();
                sk.attribute = attr;
                sk.adjLevel = val;
                if (remainingVal >= proficiencyBonus * 2) {
                    sk.level = 'expert';
                    sk.bonus = (proficiencyBonus * 2) - remainingVal;
                } else if (remainingVal >= proficiencyBonus) {
                    sk.level = 'proficient';
                    sk.bonus = proficiencyBonus - remainingVal;
                } else {
                    continue;
                }
                skillSet.skills[name] = sk;
            }
        }
        return SkillSet.updateLevels(skillSet, attributes, proficiencyBonus);
    }

}
export default SkillSet;